import { Link2, Minus, Plus, X } from 'lucide-react';
import { Fragment, useEffect, useRef, useState } from 'react';

import logoBlack from '@/assets/brand/signcare-logo.svg';
import { useCopyWorkflowLink } from '@/features/sessions/api/useCopyWorkflowLink';
import { cn } from '@/lib/utils';

import {
  WELCOME_HEADING_DEFAULT,
  WELCOME_HEADING_MAX_LENGTH,
  WELCOME_SUBHEADING_DEFAULT,
  WELCOME_SUBHEADING_MAX_LENGTH,
} from './simple/preview-screens';

// Workflow-level settings persisted as a JSON blob on the workflow (workflows.settings_json).
// smsMessage / emailMessage are the notification templates the applicant receives with
// their verification link — configured here, applied later when a session is dispatched
// to a mobile number / email. `{link}` is the placeholder for the verification URL.
export interface WorkflowSettings {
  /** Blob schema version — see CURRENT_SETTINGS_VERSION. */
  version: number;
  callbackUrl: string;
  /**
   * Override the welcome screen's two lines of copy. When off (or when a field is
   * blank) the applicant sees the built-in defaults — WELCOME_HEADING_DEFAULT /
   * WELCOME_SUBHEADING_DEFAULT.
   */
  showWelcomeScreen: boolean;
  welcomeHeading: string;
  welcomeSubheading: string;
  /**
   * Whether the verification link may be opened on a desktop / laptop. ON by
   * default; turning it off restricts the run to mobile browsers.
   */
  allowDesktopAccess: boolean;
  /** Show a progress bar during the run. Stored only — not applied yet. */
  showProgressBar: boolean;
  maxRetryAttempts: number;
  retryAfterDays: number;
  sessionValidity: string;
  smsMessage: string;
  emailMessage: string;
}

const DEFAULT_SMS_MESSAGE =
  'Dear User, please complete your verification. Click the link below.\n{link}';
const DEFAULT_EMAIL_MESSAGE = 'Dear User, please complete your verification. Click the link below.';

/**
 * Schema version of the stored blob, so defaults can change safely.
 *
 * v1 introduced Welcome Screen / Display progress bar, dropped the two dead flags
 * (`includeCustomStyle`, `allowVendorSessions`), and flipped `allowDesktopAccess`
 * to default ON. Pre-v1 blobs are migrated in `parseWorkflowSettings`.
 */
const CURRENT_SETTINGS_VERSION = 1;

export const DEFAULT_WORKFLOW_SETTINGS: WorkflowSettings = {
  version: CURRENT_SETTINGS_VERSION,
  callbackUrl: '',
  showWelcomeScreen: false,
  welcomeHeading: '',
  welcomeSubheading: '',
  // Desktop is allowed unless the workflow opts out, so existing links keep
  // working and a new workflow isn't accidentally mobile-only.
  allowDesktopAccess: true,
  showProgressBar: false,
  maxRetryAttempts: 4,
  retryAfterDays: 10,
  sessionValidity: '',
  smsMessage: DEFAULT_SMS_MESSAGE,
  emailMessage: DEFAULT_EMAIL_MESSAGE,
};

/**
 * Parse the stored JSON into a full settings object.
 *
 * Only known keys are carried over, so blobs written before v1 shed their dead
 * flags on the next save instead of lingering forever. Absent keys fall back to
 * defaults, so older workflows still open cleanly, and the welcome copy is clamped
 * here as well as on input — a value stored before the caps existed can't break
 * the phone layout.
 */
export function parseWorkflowSettings(json: string | null | undefined): WorkflowSettings {
  if (!json) return { ...DEFAULT_WORKFLOW_SETTINGS };
  let raw: Partial<WorkflowSettings>;
  try {
    raw = JSON.parse(json) as Partial<WorkflowSettings>;
  } catch {
    return { ...DEFAULT_WORKFLOW_SETTINGS };
  }

  const d = DEFAULT_WORKFLOW_SETTINGS;
  const storedVersion = typeof raw.version === 'number' ? raw.version : 0;

  return {
    version: CURRENT_SETTINGS_VERSION,
    callbackUrl: raw.callbackUrl ?? d.callbackUrl,
    showWelcomeScreen: raw.showWelcomeScreen ?? d.showWelcomeScreen,
    welcomeHeading: (raw.welcomeHeading ?? d.welcomeHeading).slice(0, WELCOME_HEADING_MAX_LENGTH),
    welcomeSubheading: (raw.welcomeSubheading ?? d.welcomeSubheading).slice(
      0,
      WELCOME_SUBHEADING_MAX_LENGTH,
    ),
    // Pre-v1 blobs stored allowDesktopAccess:false by default, and nothing ever
    // read it — so that `false` records no decision anyone made. Honouring it now
    // that the flag is enforced would silently turn every existing workflow
    // mobile-only, so pre-v1 adopts the new default instead.
    allowDesktopAccess:
      storedVersion < 1 ? d.allowDesktopAccess : (raw.allowDesktopAccess ?? d.allowDesktopAccess),
    showProgressBar: raw.showProgressBar ?? d.showProgressBar,
    maxRetryAttempts: raw.maxRetryAttempts ?? d.maxRetryAttempts,
    retryAfterDays: raw.retryAfterDays ?? d.retryAfterDays,
    sessionValidity: raw.sessionValidity ?? d.sessionValidity,
    smsMessage: raw.smsMessage ?? d.smsMessage,
    emailMessage: raw.emailMessage ?? d.emailMessage,
  };
}

/**
 * The welcome copy to render for a workflow: the overrides when the option is on
 * and the field is filled, otherwise undefined so WelcomeScreen uses its default.
 * One place decides this, so the builder preview and the live run agree.
 */
export function resolveWelcomeCopy(settings: WorkflowSettings): {
  heading?: string;
  subheading?: string;
} {
  if (!settings.showWelcomeScreen) return {};
  return {
    heading: settings.welcomeHeading.trim() || undefined,
    subheading: settings.welcomeSubheading.trim() || undefined,
  };
}

// Workflow-level Settings drawer (opened from the gear in the builder header).
// Three tabs — Settings / SMS / Email. All fields (options, retry, session, and the
// SMS/Email notification templates) are controlled and persist to the workflow's
// settings_json on the host's Save. The SMS/Email templates are stored now and
// applied later when a session is dispatched to a mobile number / email. Adapted
// from the designer reference (General/react-new-html) to the Studio design system:
// our tokens (black-logo / gray66 / grayEF / primary / green00), sc-input / sc-select,
// and the same slide-in aside as StepSettingsDrawer.

interface NumberStepperProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
}

function NumberStepper({ value, onChange, min = 0, max = 99 }: NumberStepperProps) {
  return (
    <div className="inline-flex max-w-max items-center gap-3 rounded-lg border border-[#B3B3B3] px-3 py-1">
      <button
        type="button"
        onClick={() => onChange(Math.max(min, value - 1))}
        aria-label="Decrease"
        className="flex items-center justify-center text-gray66 transition-colors hover:text-primary"
      >
        <Minus size={14} />
      </button>
      <span className="min-w-[1.5ch] text-center text-base font-semibold text-primary tabular-nums">
        {value}
      </span>
      <button
        type="button"
        onClick={() => onChange(Math.min(max, value + 1))}
        aria-label="Increase"
        className="flex items-center justify-center text-gray66 transition-colors hover:text-primary"
      >
        <Plus size={14} />
      </button>
    </div>
  );
}

const SESSION_VALIDITY_OPTIONS = [
  { value: '1h', label: '1 Hour' },
  { value: '24h', label: '24 Hours' },
  { value: '7d', label: '7 Days' },
  { value: '30d', label: '30 Days' },
];

// Reusable option checkbox (title + description). Native checkbox tinted with our
// primary accent so it matches the rest of the form controls.
function OptionCheckbox({
  checked,
  onChange,
  title,
  description,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  title: string;
  description: string;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-primary"
      />
      <span className="flex flex-col gap-1">
        <span className="text-sm font-medium text-black-logo">{title}</span>
        <span className="text-sm text-gray66">{description}</span>
      </span>
    </label>
  );
}

/**
 * Short text input with a hard character cap and a live counter.
 *
 * `maxLength` stops further typing outright rather than warning after the fact —
 * these strings render inside a fixed-width phone screen, so an over-long value
 * is a broken layout, not a style preference. The counter turns amber on the last
 * few characters so the limit isn't a surprise.
 */
function LimitedTextField({
  label,
  value,
  onChange,
  maxLength,
  placeholder,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  maxLength: number;
  placeholder: string;
  disabled?: boolean;
}) {
  const remaining = maxLength - value.length;
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-medium text-black-logo">{label}</span>
        <span
          className={cn(
            'text-xs tabular-nums',
            remaining <= 5 ? 'font-medium text-orangeDA' : 'text-gray66',
          )}
        >
          {value.length}/{maxLength}
        </span>
      </div>
      <input
        className="sc-input text-sm placeholder:normal-case disabled:cursor-not-allowed disabled:bg-[#FAFAFA] disabled:text-placeholder"
        value={value}
        maxLength={maxLength}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value.slice(0, maxLength))}
        placeholder={placeholder}
      />
    </div>
  );
}

// A bordered card with a floating label (mimics the designer's fieldset/legend).
function Field({
  label,
  action,
  children,
}: {
  label: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <fieldset className="relative m-0 min-w-0 rounded-xl border border-grayEF px-4 py-3">
      <legend className="px-1.5 text-base font-medium text-black-logo">{label}</legend>
      {action && <div className="absolute end-4 top-0 bg-white">{action}</div>}
      {children}
    </fieldset>
  );
}

// A sample verification URL shown in the previews wherever the `{link}` token
// appears (the real per-session link is substituted when the message is sent).
const SAMPLE_LINK = 'https://verify.signcare.io/s/xxxxxxxx';

// Renders the message text, turning every `{link}` token into a styled link.
// Keys are prefix-offset based (stable + unique — parts never reorder).
function renderWithLink(message: string) {
  const parts = message.split('{link}');
  let offset = 0;
  return parts.map((part, i) => {
    const key = `p${offset}`;
    offset += part.length + 1;
    return (
      <Fragment key={key}>
        {part}
        {i < parts.length - 1 && (
          <span className="break-all text-primary underline">{SAMPLE_LINK}</span>
        )}
      </Fragment>
    );
  });
}

// SMS / Email notification-template tab: a message editor + a live preview. The
// template is stored on the workflow settings and applied when a session is later
// dispatched to a mobile number / email.
function MessageTab({
  kind,
  workflowName,
  message,
  onChange,
}: {
  kind: 'sms' | 'email';
  workflowName: string;
  message: string;
  onChange: (value: string) => void;
}) {
  const label = kind === 'sms' ? 'SMS' : 'Email';
  const title = workflowName.trim() || 'Verification';

  return (
    <div className="flex flex-col gap-5">
      <Field label={label}>
        <textarea
          rows={5}
          value={message}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Message sent to the applicant with their verification link. Use {link} where the link should appear."
          className="w-full resize-y border-none bg-transparent text-base leading-relaxed text-black-logo outline-none placeholder:text-placeholder"
        />
      </Field>

      <div className="flex flex-col gap-3">
        <h3 className="m-0 text-lg font-bold text-black-logo">Preview</h3>
        {kind === 'email' ? (
          <EmailPreview title={title} message={message} />
        ) : (
          <SmsPreview message={message} />
        )}
      </div>
    </div>
  );
}

// Email preview — the SignCare-branded card an applicant receives. The `{link}`
// token is represented by the call-to-action button, so it is stripped from the body.
function EmailPreview({ title, message }: { title: string; message: string }) {
  const body = message.split('{link}').join('').trim();
  return (
    <div className="rounded-xl bg-[#F4F6FD] p-8">
      <div className="mx-auto flex max-w-[360px] flex-col items-center gap-4 rounded-xl border border-grayEF bg-white px-6 py-8 text-center shadow-[0px_2px_12px_0px_rgba(0,0,0,0.08)]">
        <img src={logoBlack} alt="SignCare" className="h-7 w-auto" />
        <h4 className="m-0 text-lg font-bold text-black-logo">{title}</h4>
        <p className="m-0 whitespace-pre-line text-sm leading-relaxed text-gray66">{body}</p>
        <span className="mt-1 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-white">
          Complete your Verification
        </span>
      </div>
    </div>
  );
}

// SMS preview — a chat-bubble rendering; the `{link}` token is shown inline as a link.
function SmsPreview({ message }: { message: string }) {
  return (
    <div className="rounded-xl bg-[#F4F6FD] p-6">
      <div className="ms-auto max-w-[300px] rounded-2xl rounded-tr-sm bg-[#E7ECFB] px-4 py-3">
        <p className="m-0 whitespace-pre-line break-words text-sm leading-relaxed text-black-logo">
          {renderWithLink(message)}
        </p>
      </div>
    </div>
  );
}

export interface WorkflowSettingsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /** Shared public-link slug (/wf/{slug}) for Copy link. */
  publicSlug?: string;
  /** Kept in sync with the builder header's name input. */
  workflowName: string;
  onWorkflowNameChange: (name: string) => void;
  /** Controlled settings value + updater (persisted on the workflow). */
  value: WorkflowSettings;
  onChange: (next: WorkflowSettings) => void;
  /** Read-only view mode disables every control. */
  readOnly?: boolean;
}

type SettingsTab = 'settings' | 'sms' | 'email';

const TABS: { id: SettingsTab; label: string }[] = [
  { id: 'settings', label: 'Settings' },
  { id: 'sms', label: 'SMS' },
  { id: 'email', label: 'Email' },
];

export function WorkflowSettingsDrawer({
  isOpen,
  onClose,
  publicSlug,
  workflowName,
  onWorkflowNameChange,
  value,
  onChange,
  readOnly = false,
}: WorkflowSettingsDrawerProps) {
  const [activeTab, setActiveTab] = useState<SettingsTab>('settings');
  const copyLink = useCopyWorkflowLink();

  // Reset to the Settings tab each time the drawer is opened.
  const wasOpen = useRef(isOpen);
  useEffect(() => {
    if (isOpen && !wasOpen.current) setActiveTab('settings');
    wasOpen.current = isOpen;
  }, [isOpen]);

  if (!isOpen) return null;

  // Patch a single field of the controlled settings object.
  const set = <K extends keyof WorkflowSettings>(key: K, v: WorkflowSettings[K]) =>
    onChange({ ...value, [key]: v });

  const handleCopyLink = () => {
    if (publicSlug) copyLink.copy(publicSlug);
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} aria-hidden="true" />
      <aside className="fixed top-0 end-0 z-40 flex h-dvh w-full max-w-[600px] flex-col bg-white shadow-elevation-2 animate-in slide-in-from-right-4 duration-200">
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between px-5 py-4">
          <h2 className="m-0 text-xl font-bold text-black-logo">Settings</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#F4F6FD] text-gray66 transition-colors hover:bg-grayEF"
          >
            <X size={18} />
          </button>
        </div>

        {/* Tabs — Settings / SMS / Email. Switchable even in read-only view. */}
        <div className="flex shrink-0 gap-6 border-b border-grayEF px-5">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setActiveTab(t.id)}
              className={cn(
                'relative -mb-px border-b-2 pb-2.5 pt-1 text-base font-medium transition-colors',
                activeTab === t.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray66 hover:text-black-logo',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <fieldset
          disabled={readOnly}
          className="flex flex-1 flex-col gap-5 overflow-y-auto px-5 py-6 custom-scrollbar"
        >
          {activeTab === 'sms' && (
            <MessageTab
              kind="sms"
              workflowName={workflowName}
              message={value.smsMessage}
              onChange={(v) => set('smsMessage', v)}
            />
          )}

          {activeTab === 'email' && (
            <MessageTab
              kind="email"
              workflowName={workflowName}
              message={value.emailMessage}
              onChange={(v) => set('emailMessage', v)}
            />
          )}

          {activeTab === 'settings' && (
            <>
              {/* Workflow Name */}
              <Field
                label="Workflow Name"
                action={
                  <button
                    type="button"
                    onClick={handleCopyLink}
                    disabled={!publicSlug}
                    className="flex items-center gap-1.5 border-none bg-transparent text-sm font-medium text-primary hover:underline disabled:opacity-50"
                  >
                    <Link2 size={14} />
                    Copy link
                  </button>
                }
              >
                <div className="flex items-center gap-2">
                  <input
                    className="min-w-0 flex-1 border-none bg-transparent text-base text-black-logo outline-none placeholder:text-placeholder"
                    value={workflowName}
                    onChange={(e) => onWorkflowNameChange(e.target.value)}
                    placeholder="Workflow name"
                  />
                  {workflowName && (
                    <button
                      type="button"
                      onClick={() => onWorkflowNameChange('')}
                      aria-label="Clear workflow name"
                      className="flex shrink-0 items-center justify-center border-none bg-transparent text-placeholder hover:text-gray66"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              </Field>

              {/* Callback URL */}
              <Field label="Callback URL">
                <input
                  className="w-full border-none bg-transparent text-base text-black-logo outline-none placeholder:text-placeholder"
                  value={value.callbackUrl}
                  onChange={(e) => set('callbackUrl', e.target.value)}
                  placeholder="https://example.com"
                />
              </Field>

              {/* Options */}
              <div className="flex flex-col gap-4">
                <h3 className="m-0 text-lg font-bold text-black-logo">Options</h3>
                <div className="flex flex-col gap-5">
                  <div className="flex flex-col gap-3">
                    <OptionCheckbox
                      checked={value.showWelcomeScreen}
                      onChange={(v) => set('showWelcomeScreen', v)}
                      title="Welcome Screen"
                      description="Do you want to show Welcome Screen on starting of workflow?"
                    />
                    {/* Always visible (as in the design), but only applied when the
                        option is on — see resolveWelcomeCopy. */}
                    <div className="flex flex-col gap-3 ps-7">
                      <LimitedTextField
                        label="Heading of Welcome Screen"
                        value={value.welcomeHeading}
                        onChange={(v) => set('welcomeHeading', v)}
                        maxLength={WELCOME_HEADING_MAX_LENGTH}
                        placeholder={WELCOME_HEADING_DEFAULT}
                        disabled={!value.showWelcomeScreen}
                      />
                      <LimitedTextField
                        label="Text below Heading"
                        value={value.welcomeSubheading}
                        onChange={(v) => set('welcomeSubheading', v)}
                        maxLength={WELCOME_SUBHEADING_MAX_LENGTH}
                        placeholder={WELCOME_SUBHEADING_DEFAULT}
                        disabled={!value.showWelcomeScreen}
                      />
                    </div>
                  </div>
                  <OptionCheckbox
                    checked={value.allowDesktopAccess}
                    onChange={(v) => set('allowDesktopAccess', v)}
                    title="Allow desktop access"
                    description="Desktop camera quality impacts verification. Passive liveness included. Turn this off to restrict the verification link to mobile devices."
                  />
                  <OptionCheckbox
                    checked={value.showProgressBar}
                    onChange={(v) => set('showProgressBar', v)}
                    title="Display progress bar"
                    description="Do you want to show Progress bar in workflow?"
                  />
                </div>
              </div>

              {/* Retry Settings */}
              <Field label="Retry Settings">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div className="flex flex-col gap-1.5">
                    <h4 className="m-0 text-sm font-medium text-black-logo">Max retry attempts</h4>
                    <p className="m-0 mb-2 text-xs leading-snug text-gray66 xl:mb-4">
                      Set the maximum failed attempts before retries are blocked. Set to 0 to
                      disable retries.
                    </p>
                    <NumberStepper
                      value={value.maxRetryAttempts}
                      onChange={(v) => set('maxRetryAttempts', v)}
                      min={0}
                      max={20}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <h4 className="m-0 text-sm font-medium text-black-logo">Retry After (days)</h4>
                    <p className="m-0 mb-2 text-xs leading-snug text-gray66 xl:mb-4">
                      Set the number of days to count failed attempts. Leave blank to count all
                      attempts.
                    </p>
                    <NumberStepper
                      value={value.retryAfterDays}
                      onChange={(v) => set('retryAfterDays', v)}
                      min={0}
                      max={90}
                    />
                  </div>
                </div>
              </Field>

              {/* Session Expiry */}
              <Field label="Session Expiry">
                <div className="flex flex-col gap-1.5">
                  <h4 className="m-0 text-sm font-medium text-black-logo">Session Validity</h4>
                  <p className="m-0 mb-2 text-xs leading-snug text-gray66 xl:mb-4">
                    Sets how long a new session stays active before it expires if not completed.
                  </p>
                  <select
                    className={cn(
                      'sc-select w-full text-sm',
                      !value.sessionValidity && 'text-placeholder',
                    )}
                    value={value.sessionValidity}
                    onChange={(e) => set('sessionValidity', e.target.value)}
                  >
                    <option value="">Select session validity</option>
                    {SESSION_VALIDITY_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value} className="text-black-logo">
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              </Field>
            </>
          )}
        </fieldset>
      </aside>
    </>
  );
}
